import { v4 as uuidv4 } from 'uuid'

import { VariableSelectFeatureFlag } from '@makyoapp/makyo-common'

import { IBuilderComponent } from '../../base'
import type {
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TextAlignment,
  TypographyValue,
  VariableSelectValue,
  VariableTextValue,
} from '../../types'
import {
  formatCornerRadiusFormat,
  formatFromVariableSelect,
  formatFromVariableText,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseCornerRadiusFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import audioTemplate from './audio.template.json'

export interface IAudioComponentSettings {
  // Data settings
  'audio-data-src': VariableSelectValue
  'audio-data-cover': VariableSelectValue
  'audio-data-header': VariableTextValue
  'audio-data-subheader': VariableTextValue
  'audio-data-text': VariableTextValue

  // Design settings
  'audio-design-scheme': any
  'audio-design-color-variant': string
  'audio-design-opacity': any
  'audio-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'audio-design-shape': string
  'audio-design-padding': ResponsiveValue<SpacingInputValue>
  'audio-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'audio-design-h-align': ResponsiveValue<TextAlignment>
  'audio-design-typography': {
    'audio-design-header-typography': ResponsiveValue<TypographyValue>
    'audio-design-subheader-typography': ResponsiveValue<TypographyValue>
    'audio-design-supporting-text-typography': ResponsiveValue<TypographyValue>
    'audio-design-timer-typography': ResponsiveValue<TypographyValue>
  }
  'audio-design-hover-effect'?: string // Hover effect: 'none' | 'underline' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
}

export interface IAudioComponent extends IBuilderComponent {
  componentId: 'audio'
  groupId: 'media'
  settings: IAudioComponentSettings
}

export interface AiAudioInput {
  src: string // Audio URL
  cover?: string // Cover image URL
  header?: string[] // Format: ["text:Audio Title"] or ["text:Song: ", "cr:title"]
  subheader?: string[] // Format: ["text:Artist Name"] or ["text:By ", "cr:artist"]
  text?: string[] // Format: ["text:Description"] or ["text:Album: ", "cr:album"]
  width?: string // Format: "w:100%" or "w:300px" or "w:100% de:w:50%"
  margin?: string // Format: "m:10px" or "mt:10px mb:10px"
  padding?: string // Format: "p:10px"
  align?: string // Format: "h:center" or "h:start ta:h:center"
  cornerRadius?: string // Format: "large" or "small ta:medium de:large"
  typography?: string // Format: "body-medium" or "body-medium ta:body-large"
  hoverEffect?: string // Format: "bounce" or "underline" | 'none' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
}

const AUDIO_DEFAULT_TEMPLATE = audioTemplate as unknown as IAudioComponentSettings

export class AudioComp {
  getTemplate(): IAudioComponentSettings {
    return AUDIO_DEFAULT_TEMPLATE
  }

  /**
   * format audio component settings to AI audio input
   */
  format(settings: IAudioComponentSettings): AiAudioInput {
    const template = this.getTemplate()
    const result: AiAudioInput = {
      src: formatFromVariableSelect(settings['audio-data-src']) || '',
    }

    // Cover
    if (settings['audio-data-cover']) {
      const cover = formatFromVariableSelect(settings['audio-data-cover'])
      if (cover) {
        result.cover = cover
      }
    }

    // Header
    if (settings['audio-data-header']) {
      const header = formatFromVariableText(settings['audio-data-header'])
      if (header.length > 0 && header[0] !== 'text:') {
        result.header = header
      }
    }

    // Subheader
    if (settings['audio-data-subheader']) {
      const subheader = formatFromVariableText(settings['audio-data-subheader'])
      if (subheader.length > 0 && subheader[0] !== 'text:') {
        result.subheader = subheader
      }
    }

    // Text
    if (settings['audio-data-text']) {
      const text = formatFromVariableText(settings['audio-data-text'])
      if (text.length > 0 && text[0] !== 'text:') {
        result.text = text
      }
    }

    // Width - extract from OverflowSettings
    if (settings['audio-design-width']) {
      const width = formatWidthFormat(settings['audio-design-width'] as any)
      if (width) {
        result.width = width
      }
    }

    // Margin - extract from OverflowSettings
    if (settings['audio-design-margin']) {
      const margin = formatSpacingFormat(settings['audio-design-margin'] as any)
      if (margin) {
        result.margin = margin
      }
    }

    // Padding
    const padding = formatSpacingFormat(settings['audio-design-padding'])
    if (padding) {
      result.padding = padding
    }

    // Alignment
    if (settings['audio-design-h-align']) {
      const textAlign = settings['audio-design-h-align'].phone
      if (textAlign && (textAlign as any) !== 'parent') {
        const alignMap: Record<string, string> = {
          left: 'h:start',
          right: 'h:end',
          center: 'h:center',
        }
        result.align = alignMap[textAlign] || 'h:center'
      }
    }

    // Corner Radius
    const cornerRadius = formatCornerRadiusFormat(settings['audio-design-shape'])
    if (cornerRadius && cornerRadius !== template['audio-design-shape']) {
      result.cornerRadius = cornerRadius
    }

    // Typography - use header typography
    if (settings['audio-design-typography']?.['audio-design-header-typography']) {
      const typography = formatTypographyFormat(
        settings['audio-design-typography']['audio-design-header-typography']
      )
      if (typography) {
        result.typography = typography
      }
    }

    // Hover Effect
    if (settings['audio-design-hover-effect']) {
      const hoverEffect = settings['audio-design-hover-effect']
      if (hoverEffect && hoverEffect !== 'none') {
        result.hoverEffect = hoverEffect
      }
    }

    return result
  }

  /**
   * parse AI audio input to audio component settings
   */
  parse(input: AiAudioInput): IAudioComponentSettings {
    const result = deepMerge(AUDIO_DEFAULT_TEMPLATE, {})

    // Data - Audio source (required)
    if (input.src) {
      result['audio-data-src'] = {
        id: uuidv4(),
        value: input.src,
        flag: VariableSelectFeatureFlag.CustomText,
      }
    }

    // Data - Cover image
    if (input.cover) {
      result['audio-data-cover'] = {
        id: uuidv4(),
        value: input.cover,
        flag: VariableSelectFeatureFlag.Image,
      }
    }

    // Data - Text content
    if (input.header) {
      result['audio-data-header'] = parseToVariableText(input.header)
    }

    if (input.subheader) {
      result['audio-data-subheader'] = parseToVariableText(input.subheader)
    }

    if (input.text) {
      result['audio-data-text'] = parseToVariableText(input.text)
    }

    // Design - Width
    if (input.width) {
      result['audio-design-width'] = {
        ...parseWidthFormat(input.width),
        overflowSetting: result['audio-design-width'].overflowSetting,
      }
    }

    // Design - Margin
    if (input.margin) {
      result['audio-design-margin'] = {
        ...parseSpacingFormat(input.margin),
        overflowSetting: result['audio-design-margin'].overflowSetting,
      }
    }

    // Design - Padding
    if (input.padding) {
      result['audio-design-padding'] = parseSpacingFormat(input.padding)
    }

    // Design - Alignment
    if (input.align) {
      const alignmentParsed = parseAlignmentFormat(input.align)
      if (alignmentParsed.hAlign) {
        // Map ComponentAlignment to TextAlignment
        const mappedTextAlign = Object.fromEntries(
          Object.entries(alignmentParsed.hAlign).map(([key, value]) => {
            let textAlign: TextAlignment
            if (value === 'start') textAlign = 'left'
            else if (value === 'end') textAlign = 'right'
            else textAlign = 'center'
            return [key, textAlign]
          })
        ) as ResponsiveValue<TextAlignment>
        result['audio-design-h-align'] = mappedTextAlign
      }
      if (alignmentParsed.textAlign) {
        result['audio-design-h-align'] = alignmentParsed.textAlign
      }
    }

    // Design - Corner Radius
    if (input.cornerRadius) {
      result['audio-design-shape'] = parseCornerRadiusFormat(input.cornerRadius).phone
    }

    // Design - Typography (nested structure)
    if (input.typography) {
      const parsedTypography = parseTypographyFormat(input.typography)
      result['audio-design-typography']['audio-design-header-typography'] = parsedTypography
      result['audio-design-typography']['audio-design-subheader-typography'] = parsedTypography
      result['audio-design-typography']['audio-design-supporting-text-typography'] = parsedTypography
      result['audio-design-typography']['audio-design-timer-typography'] = parsedTypography
    }

    // Hover Effect
    if (input.hoverEffect) {
      result['audio-design-hover-effect'] = input.hoverEffect
    }

    return result
  }
}

